import React, { useState, useEffect, useRef, useMemo } from "react";
import { Mail, Info } from "lucide-react";
import Select from "react-select";
import { ExternalLinkIcon, XIcon } from "../../components/icons";
import Toast from "../../common/Toast/Toast";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import { IoCloseCircleOutline } from "react-icons/io5";
import { emailccRegex } from "../../configs/App.Regex";
import { ImAttachment } from "react-icons/im";
import { FiFileText } from "react-icons/fi";
import WarningIcon from "../../resources/icons/warning-info.svg";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const TEMPLATE_PAGE_LIMIT = 100;
const NOTIFY_ALL_PAGE_LIMIT = 10;

const EmailPreviewModal = ({
  recipient,
  recipients,
  isOpen,
  onClose,
  isMultipleSelect,
  isNotifyAll = false,
  toEmails,
  currentToEmail,
  toEmailError,
  onToEmailChange,
  onAddToEmail,
  onRemoveToEmail,
  onPasteToEmail,
  ccEmails,
  setCcEmails,
  currentCcEmail,
  ccEmailError,
  onCcEmailChange,
  onAddCcEmail,
  onRemoveCcEmail,
  onPasteCcEmail,
  initialTemplateId = "",
  issuerCredentialTemplateId = "",
  onRequestConfirmation,
  recipientsPendingCount = 0,
  recipientsIssuedCount = 0,
  exclusionNotice = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedRecipientId, setSelectedRecipientId] = useState("");
  const [emailMetadata, setEmailMetadata] = useState(null);
  const [emailPreview, setEmailPreview] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    initialTemplateId || "",
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const [recipientToEmails, setRecipientToEmails] = useState({});
  const [recipientCcEmails, setRecipientCcEmails] = useState({});
  const [currentMultiToEmail, setCurrentMultiToEmail] = useState("");
  const [currentMultiCcEmail, setCurrentMultiCcEmail] = useState("");
  const [multiToEmailError, setMultiToEmailError] = useState("");
  const [multiCcEmailError, setMultiCcEmailError] = useState("");
  const [activeField, setActiveField] = useState(null);
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const [isEmailIdentity, setIsEmailIdentity] = useState(false);
  const [emailIdentityMessage, setIsEmailIdentityMessage] = useState("");
  const metadataRequestSeqRef = useRef(0);
  const previewRequestSeqRef = useRef(0);
  const notifyAllRequestSeqRef = useRef(0);
  const [notifyAllRecipients, setNotifyAllRecipients] = useState([]);
  const [notifyAllOffset, setNotifyAllOffset] = useState(0);
  const [notifyAllHasMore, setNotifyAllHasMore] = useState(true);
  const [isLoadingNotifyAllRecipients, setIsLoadingNotifyAllRecipients] =
    useState(false);
  const [notifyAllSearchTerm, setNotifyAllSearchTerm] = useState("");
  const notifyAllSearchTimerRef = useRef(null);

  const effectiveRecipients = useMemo(
    () => (isNotifyAll ? notifyAllRecipients : recipients),
    [isNotifyAll, notifyAllRecipients, recipients],
  );

  useEffect(() => {
    if (isOpen && isMultipleSelect && recipients.length > 0) {
      const initialEmails = {};
      const initialCcEmailsMap = {};
      recipients.forEach((recipient) => {
        initialEmails[recipient.id] = [recipient.email];
        initialCcEmailsMap[recipient.id] = [];
      });
      setRecipientToEmails(initialEmails);
      setRecipientCcEmails(initialCcEmailsMap);
      setSelectedRecipientId(recipients[0].id);
    }
  }, [isOpen, isMultipleSelect, recipients]);

  useEffect(() => {
    if (isOpen && isNotifyAll && issuerCredentialTemplateId) {
      if (notifyAllSearchTimerRef.current) {
        clearTimeout(notifyAllSearchTimerRef.current);
        notifyAllSearchTimerRef.current = null;
      }
      setNotifyAllSearchTerm("");
      setSelectedRecipientId("");
      setNotifyAllRecipients([]);
      setNotifyAllOffset(0);
      setNotifyAllHasMore(true);
      fetchNotifyAllRecipients(0, true);
    }
  }, [isOpen, isNotifyAll, issuerCredentialTemplateId]);

  useEffect(() => {
    if (
      isOpen &&
      isNotifyAll &&
      notifyAllRecipients.length > 0 &&
      !selectedRecipientId
    ) {
      setSelectedRecipientId(notifyAllRecipients[0].id);
    }
  }, [isOpen, isNotifyAll, notifyAllRecipients, selectedRecipientId]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTemplateId(initialTemplateId || "");
  }, [isOpen, initialTemplateId]);

  useEffect(() => {
    if (!isOpen) return;
    fetchEmailTemplates();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const credentialId =
      isMultipleSelect || isNotifyAll ? selectedRecipientId : recipient?.id;
    if (!credentialId) return;

    const payload = selectedTemplateId
      ? { credential_id: credentialId, email_template: selectedTemplateId }
      : { credential_id: credentialId };
    fetchEmailMetadata(payload);
    fetchEmailPreview(payload);
  }, [
    isOpen,
    isMultipleSelect,
    isNotifyAll,
    selectedRecipientId,
    recipient?.id,
    selectedTemplateId,
  ]);

  const fetchEmailMetadata = async (payload) => {
    const requestSeq = ++metadataRequestSeqRef.current;
    try {
      const response = await POST(
        EntityConstants.POST_ISSUANCE_EMAIL_METADATA,
        payload,
      );

      const data = await response.json();

      if (requestSeq !== metadataRequestSeqRef.current) return;

      if (!response.ok) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data?.message || t("workspace:failed_fetch_email_data"),
        });
        return;
      }

      const templateData = data?.email_templates;

      if (!templateData) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:invalid_email_metadata_response"),
        });
        return;
      }
      setEmailMetadata(templateData);

      const returnedTemplateId = templateData.template_id || "";
      if (returnedTemplateId) {
        setSelectedTemplateId((prev) => prev || returnedTemplateId);
      }

      setIsEmailIdentity(templateData.is_email_identity);
      setIsEmailIdentityMessage(templateData.message);

      if (
        data.email_templates?.email_cc &&
        Array.isArray(data.email_templates.email_cc)
      ) {
        const credentialId = payload?.credential_id;

        if (isMultipleSelect) {
          if (credentialId && !recipientCcEmails[credentialId]?.length) {
            setRecipientCcEmails((prev) => ({
              ...prev,
              [credentialId]: [...data.email_templates.email_cc],
            }));
          }
        } else {
          if (!ccEmails.length) {
            setCcEmails([...data.email_templates.email_cc]);
          }
        }
      }
    } catch (error) {
      if (requestSeq !== metadataRequestSeqRef.current) return;

      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_email_data"),
      });
    }
  };

  // Add this validation function
  const validateToEmails = () => {
    if (isMultipleSelect) {
      // Check if any recipient has at least one To email
      const hasValidToEmails = Object.values(recipientToEmails).some(
        (emails) => emails.length > 0,
      );

      if (!hasValidToEmails) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:at_least_one_to_email_required"),
        });
        return false;
      }

      // Check each recipient has at least one To email
      const recipientsWithoutTo = recipients.filter(
        (recipient) => !recipientToEmails[recipient.id]?.length,
      );

      if (recipientsWithoutTo.length > 0) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:all_recipients_need_to_email"),
        });
        return false;
      }
    } else {
      // Single recipient - check if toEmails has at least one email
      if (!toEmails.length) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:to_email_required"),
        });
        return false;
      }
    }
    return true;
  };

  const buildSendPayload = () => {
    if (isMultipleSelect) {
      return {
        notification_type: "email",
        email_template: selectedTemplateId,
        credentials_id: recipients.map((r) => r.id),
        credentials_with_cc: recipients.map((r) => ({
          credentials_id: r.id,
          to: recipientToEmails[r.id],
          cc: recipientCcEmails[r.id],
          bcc: [],
        })),
      };
    }
    return {
      notification_type: "email",
      email_template: selectedTemplateId,
      credentials_id: [recipient.id],
      credentials_with_cc: [
        {
          credentials_id: recipient.id,
          to: toEmails,
          cc: ccEmails,
          bcc: [],
        },
      ],
    };
  };

  // Update handleSendClick function
  const handleSendClick = () => {
    if (isNotifyAll && onRequestConfirmation) {
      onRequestConfirmation({
        recipientCount: recipientsPendingCount || recipientsIssuedCount,
        totalTo: recipientsPendingCount || recipientsIssuedCount,
        totalCc: 0,
        selectedTemplateId: selectedTemplateId || "",
        sendPayload: null,
        isMultipleSelect: false,
        isNotifyAll: true,
      });
      return;
    }

    // First validate that To emails are present
    if (!validateToEmails()) {
      return;
    }

    if (onRequestConfirmation) {
      const { totalTo: tTo, totalCc: tCc } = getTotalEmailCounts();
      onRequestConfirmation({
        recipientCount: isMultipleSelect ? recipients.length : 1,
        totalTo: tTo,
        totalCc: tCc,
        sendPayload: buildSendPayload(),
        isMultipleSelect: Boolean(isMultipleSelect),
        isNotifyAll: false,
      });
      return;
    }

    handleSendEmails();
  };

  const fetchEmailTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const response = await GET(
        `${EntityConstants.EMAIL_TEMPLATES}?limit=${TEMPLATE_PAGE_LIMIT}&offset=0`,
      );
      const data = await response.json();
      if (!response.ok) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data?.message || t("workspace:failed_fetch_email_data"),
        });
        setEmailTemplates([]);
        return;
      }
      const templateList = data?.results || data?.email_templates || [];
      setEmailTemplates(Array.isArray(templateList) ? templateList : []);
    } catch (error) {
      console.error("Error fetching email templates:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleTemplateChange = async (selectedOption) => {
    const templateId = selectedOption?.value || "";
    setSelectedTemplateId(templateId);
  };

  const fetchEmailPreview = async (payload) => {
    const requestSeq = ++previewRequestSeqRef.current;
    try {
      setIsLoading(true);
      const response = await POST(
        EntityConstants.POST_ISSUANCE_EMAIL_PREVIEW,
        payload,
      );

      const html = await response.text();

      // Check sequence before updating state
      if (requestSeq !== previewRequestSeqRef.current) return;

      if (!response.ok) {
        throw new Error(html || t("workspace:failed_fetch_email_preview"));
      }
      setEmailPreview(html);
    } catch (error) {
      // Check sequence before showing error
      if (requestSeq !== previewRequestSeqRef.current) return;

      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_email_preview"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotifyAllRecipients = async (
    offset = 0,
    isInitialLoad = false,
    search = "",
  ) => {
    if (!issuerCredentialTemplateId) return;
    const requestSeq = ++notifyAllRequestSeqRef.current;
    try {
      setIsLoadingNotifyAllRecipients(true);
      const statusFilter =
        "status=Issued,Printed,Notified,Printed_and_Notified";
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await GET(
        `${EntityConstants.GET_RECIPIENTS}/${issuerCredentialTemplateId}?limit=${NOTIFY_ALL_PAGE_LIMIT}&offset=${offset}&order_by_notification_status=not_sent&${statusFilter}${searchParam}`,
      );
      const data = await response.json();
      if (requestSeq !== notifyAllRequestSeqRef.current) return;
      if (!response.ok) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_recipients"),
        });
        return;
      }
      const transformed = (data.results || []).map((r) => ({
        id: r.credential_id,
        name: r.recipient_name,
        email: r.recipient_email,
        rollNumber: r.roll_number,
        phone: r.recipient_phone,
      }));
      if (isInitialLoad) {
        setNotifyAllRecipients(transformed);
      } else {
        setNotifyAllRecipients((prev) => [...prev, ...transformed]);
      }
      setNotifyAllHasMore(!!data.next);
      setNotifyAllOffset(offset + NOTIFY_ALL_PAGE_LIMIT);
    } catch (error) {
      if (requestSeq !== notifyAllRequestSeqRef.current) return;
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_recipients"),
      });
    } finally {
      if (requestSeq === notifyAllRequestSeqRef.current) {
        setIsLoadingNotifyAllRecipients(false);
      }
    }
  };

  const handleSendEmails = async () => {
    try {
      setIsSending(true);

      let payload;
      if (isMultipleSelect) {
        payload = {
          notification_type: "email",
          email_template: selectedTemplateId,
          credentials_id: recipients.map((r) => r.id),
          credentials_with_cc: recipients.map((recipient) => ({
            credentials_id: recipient.id,
            to: recipientToEmails[recipient.id],
            cc: recipientCcEmails[recipient.id],
            bcc: [],
          })),
        };
      } else {
        payload = {
          notification_type: "email",
          email_template: selectedTemplateId,
          credentials_id: [recipient.id],
          credentials_with_cc: [
            {
              credentials_id: recipient.id,
              to: toEmails,
              cc: ccEmails,
              bcc: [],
            },
          ],
        };
      }
      const response = await POST(EntityConstants.SEND_EMAIL, payload);
      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: t("workspace:success"),
          message: isMultipleSelect
            ? t("workspace:emails_sent_successfully")
            : t("workspace:email_sent_successfully"),
        });
        onClose();
      } else {
        throw new Error(data.message || t("workspace:failed_send_emails"));
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: error?.message || t("workspace:failed_send_emails"),
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleRecipientEmailChange = (e) => {
    const email = e.target.value;
    setCurrentMultiToEmail(email);
    if (!email || emailccRegex.test(email)) {
      setMultiToEmailError("");
    }
  };

  const handleRecipientCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentMultiCcEmail(email);
    if (!email || emailccRegex.test(email)) {
      setMultiCcEmailError("");
    }
  };

  const handleAddMultiToEmail = () => {
    if (activeField !== "to") return;
    if (emailccRegex.test(currentMultiToEmail)) {
      if (
        recipientToEmails[selectedRecipientId]?.includes(currentMultiToEmail)
      ) {
        setMultiToEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setRecipientToEmails((prev) => ({
          ...prev,
          [selectedRecipientId]: [
            ...(prev[selectedRecipientId] || []),
            currentMultiToEmail,
          ],
        }));
        setCurrentMultiToEmail("");
        setMultiToEmailError("");
      }
    } else if (currentMultiToEmail) {
      setMultiToEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleAddMultiCcEmail = () => {
    if (activeField !== "cc") return;
    if (emailccRegex.test(currentMultiCcEmail)) {
      if (
        recipientCcEmails[selectedRecipientId]?.includes(currentMultiCcEmail)
      ) {
        setMultiCcEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setRecipientCcEmails((prev) => ({
          ...prev,
          [selectedRecipientId]: [
            ...(prev[selectedRecipientId] || []),
            currentMultiCcEmail,
          ],
        }));
        setCurrentMultiCcEmail("");
        setMultiCcEmailError("");
      }
    } else if (currentMultiCcEmail) {
      setMultiCcEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveMultiToEmail = (emailToRemove) => {
    setRecipientToEmails((prev) => ({
      ...prev,
      [selectedRecipientId]: prev[selectedRecipientId].filter(
        (email) => email !== emailToRemove,
      ),
    }));
  };

  const handleRemoveMultiCcEmail = (emailToRemove) => {
    setRecipientCcEmails((prev) => ({
      ...prev,
      [selectedRecipientId]: prev[selectedRecipientId].filter(
        (email) => email !== emailToRemove,
      ),
    }));
  };

  const getTotalEmailCounts = () => {
    if (isMultipleSelect) {
      const totalTo = Object.values(recipientToEmails).reduce(
        (sum, emails) => sum + emails.length,
        0,
      );
      const totalCc = Object.values(recipientCcEmails).reduce(
        (sum, emails) => sum + emails.length,
        0,
      );
      return { totalTo, totalCc };
    }
    return {
      totalTo: toEmails.length,
      totalCc: ccEmails.length,
    };
  };

  const templateOptions = emailTemplates.map((template) => ({
    value: template.template_id,
    label: template.template_name,
  }));
  const selectedTemplateOption =
    templateOptions.find((option) => option.value === selectedTemplateId) ||
    (selectedTemplateId
      ? {
          value: selectedTemplateId,
          label:
            emailMetadata?.template_name ||
            emailMetadata?.template_id ||
            selectedTemplateId,
        }
      : null) ||
    null;
  const templateSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: 42,
      borderColor: "#D0D5DD",
      boxShadow: "none",
    }),
  };

  if (!isOpen) return null;

  const Shimmer = ({ className = "" }) => (
    <div
      className={`animate-pulse rounded-lg bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] ${className}`}
      style={{ animation: "shimmer 1.5s ease-in-out infinite" }}
    />
  );

  //   /* Template selector skeleton */
  const FieldSkeleton = () => (
    <div className="space-y-7">
      <div className="rounded-xl border border-[#BFD3FF] bg-[#EEF4FF] p-4">
        <Shimmer className="h-4 w-32 mb-3" />
        <div className="flex items-center gap-2">
          <Shimmer className="h-[42px] flex-1 rounded-lg" />
          <Shimmer className="h-[42px] w-[110px] rounded-lg" />
        </div>
      </div>
      <div>
        <Shimmer className="h-4 w-16 mb-2" />
        <Shimmer className="h-[42px] w-full rounded-lg" />
      </div>
      <div>
        <Shimmer className="h-4 w-10 mb-2" />
        <Shimmer className="h-[50px] w-full rounded-lg" />
      </div>
      <div>
        <Shimmer className="h-4 w-10 mb-2" />
        <Shimmer className="h-[50px] w-full rounded-lg" />
      </div>
      <div>
        <Shimmer className="h-4 w-20 mb-2" />
        <Shimmer className="h-[42px] w-full rounded-lg" />
      </div>
    </div>
  );

  const PreviewSkeleton = () => (
    <div className="rounded-xl border border-cfIssuedScreenSearchBorder bg-cfSecondary p-6 space-y-5">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <Shimmer className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-4 w-40" />
          <Shimmer className="h-3 w-56" />
        </div>
      </div>
      <div className="space-y-3">
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-11/12" />
        <Shimmer className="h-4 w-9/12" />
      </div>
      <Shimmer className="h-36 w-full rounded-xl" />
      <div className="space-y-3">
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-10/12" />
        <Shimmer className="h-4 w-8/12" />
        <Shimmer className="h-4 w-7/12" />
      </div>
      <div className="flex justify-center pt-2">
        <Shimmer className="h-10 w-40 rounded-lg" />
      </div>
      <div className="space-y-2 pt-4 border-t border-gray-100">
        <Shimmer className="h-3 w-48 mx-auto" />
        <Shimmer className="h-3 w-36 mx-auto" />
      </div>
    </div>
  );

  return (
    <Transition appear show={isOpen} as="div" className="relative z-50">
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as="div"
          className="fixed inset-0 bg-black/40"
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        />

        <Transition.Child
          as="div"
          className="fixed inset-0 overflow-y-auto p-4 md:p-8"
          enter="ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="flex min-h-full items-center justify-center">
            <Dialog.Panel
              id="notify-email-preview-modal"
              className="w-full max-w-[1320px] overflow-hidden rounded-2xl border border-cfIssuedScreenSearchBorder bg-cfSecondary shadow-2xl"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <Dialog.Title className="text-lg font-semibold">
                  {t("workspace:send_email_notification")}
                  {isMultipleSelect ? "s" : ""}
                </Dialog.Title>
                <button
                  onClick={onClose}
                  id="notify-email-preview-modal-close"
                  aria-label={t("workspace:close")}
                  className="text-cfGlobalText hover:text-cfHeadingText transition-colors focus:outline-none"
                >
                  <XIcon size={20} className="inline-flex" aria-hidden />
                </button>
              </div>

              <div className="px-6 py-3 border-b border-gray-200 bg-[#EEF4FF]">
                <div className="flex items-center gap-2 text-cfHeadingText">
                  <Info className="h-4 w-4 text-cfPrimary" />
                  <p className="text-sm">
                    {isNotifyAll ? (
                      <>{t("workspace:notify_all_confirmation")} </>
                    ) : (
                      <>
                        {t("workspace:notifications_will_be_sent_to")}{" "}
                        <span className="font-semibold">
                          {isMultipleSelect ? recipients.length : 1}
                        </span>{" "}
                        {t("workspace:recipients_label")}
                      </>
                    )}
                  </p>
                </div>
              </div>

              {exclusionNotice ? (
                <div
                  className="px-6 py-3 border-b border-amber-200 bg-amber-50"
                  role="status"
                  id="notify-email-preview-exclusion-notice"
                >
                  <div className="flex items-start gap-2 text-amber-900">
                    <img
                      src={WarningIcon}
                      alt=""
                      className="h-10 w-10 mt-0.5 mr-0.5 shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0">
                      {exclusionNotice.title ? (
                        <p className="text-sm font-semibold">
                          {exclusionNotice.title}
                        </p>
                      ) : null}
                      <p
                        className={`text-sm ${
                          exclusionNotice.title ? "mt-0.5" : ""
                        }`}
                      >
                        {exclusionNotice.message || exclusionNotice}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col lg:flex-row min-h-[620px]">
                <div className="w-full lg:w-[520px] border-r border-gray-200 p-7 overflow-y-auto">
                  {(isMultipleSelect || isNotifyAll) && (
                    <div className="mb-6">
                      <label
                        htmlFor="recipient-select"
                        className="block text-sm font-medium text-cfPrimaryDarkBlue mb-2"
                      >
                        {t("workspace:preview_for")}:
                      </label>
                      <Select
                        inputId="recipient-select"
                        name="recipient-select"
                        autoComplete="off"
                        isSearchable
                        options={effectiveRecipients.map((r) => ({
                          value: r.id,
                          label: r.name || r.email || r.id,
                          email: r.email || "",
                          rollNumber: r.rollNumber || "",
                        }))}
                        filterOption={
                          isNotifyAll
                            ? () => true
                            : (option, inputValue) => {
                                if (!inputValue) return true;
                                const search = inputValue.toLowerCase();
                                return (
                                  (option.label || "")
                                    .toLowerCase()
                                    .includes(search) ||
                                  (option.data?.email || "")
                                    .toLowerCase()
                                    .includes(search) ||
                                  (option.data?.rollNumber || "")
                                    .toLowerCase()
                                    .includes(search)
                                );
                              }
                        }
                        formatOptionLabel={(option, { context }) => {
                          if (context === "value") return option.label;
                          return (
                            <div id={pageRootTestIds.notifyEmailPreviewModal}>
                              <div className="font-medium">{option.label}</div>
                              {option.email &&
                                option.label !== option.email && (
                                  <div className="text-xs text-gray-500">
                                    {option.email}
                                  </div>
                                )}
                              {option.rollNumber && (
                                <div className="text-xs text-gray-400">
                                  {t("workspace:roll_number_prefix")}
                                  {option.rollNumber}
                                </div>
                              )}
                            </div>
                          );
                        }}
                        value={{
                          value: selectedRecipientId,
                          label:
                            effectiveRecipients.find(
                              (r) => r.id === selectedRecipientId,
                            )?.name ||
                            effectiveRecipients.find(
                              (r) => r.id === selectedRecipientId,
                            )?.email ||
                            selectedRecipientId,
                        }}
                        onChange={(opt) =>
                          setSelectedRecipientId(opt?.value || "")
                        }
                        placeholder={t("workspace:search_recipients")}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base, state) => ({
                            ...base,
                            minHeight: 42,
                            borderWidth: 2,
                            borderColor: state.isFocused
                              ? "#3B82F6"
                              : "#BFDBFE",
                            backgroundColor: "#EFF6FF",
                            color: "#1e3a8a",
                            boxShadow: state.isFocused
                              ? "0 0 0 2px rgba(59,130,246,0.2)"
                              : "none",
                            fontWeight: 500,
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: "#1e3a8a",
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "#DBEAFE"
                              : state.isFocused
                                ? "#EFF6FF"
                                : "white",
                            color: "#1e3a8a",
                          }),
                        }}
                        menuPortalTarget={document.body}
                        onInputChange={(inputValue, { action }) => {
                          if (!isNotifyAll) return;
                          if (action === "input-change") {
                            if (notifyAllSearchTimerRef.current) {
                              clearTimeout(notifyAllSearchTimerRef.current);
                            }
                            setNotifyAllSearchTerm(inputValue);
                            notifyAllSearchTimerRef.current = setTimeout(() => {
                              setNotifyAllOffset(0);
                              setNotifyAllHasMore(true);
                              fetchNotifyAllRecipients(0, true, inputValue);
                            }, 400);
                          }
                          if (
                            action === "menu-close" ||
                            action === "set-value"
                          ) {
                            if (notifyAllSearchTimerRef.current) {
                              clearTimeout(notifyAllSearchTimerRef.current);
                            }
                            if (notifyAllSearchTerm) {
                              setNotifyAllSearchTerm("");
                              setNotifyAllOffset(0);
                              setNotifyAllHasMore(true);
                              fetchNotifyAllRecipients(0, true, "");
                            }
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (
                            isNotifyAll &&
                            notifyAllHasMore &&
                            !isLoadingNotifyAllRecipients
                          ) {
                            fetchNotifyAllRecipients(
                              notifyAllOffset,
                              false,
                              notifyAllSearchTerm,
                            );
                          }
                        }}
                        isLoading={isNotifyAll && isLoadingNotifyAllRecipients}
                      />
                      <p className="mt-2 text-sm text-cfPrimaryDarkBlue">
                        {t("workspace:select_recipient_to_preview")}
                      </p>
                    </div>
                  )}
                  {emailMetadata === null ? (
                    <FieldSkeleton />
                  ) : (
                    <div className="space-y-7">
                      <div className="rounded-xl border border-[#BFD3FF] bg-[#EEF4FF] p-4 shadow-[0_4px_10px_rgba(15,23,42,0.08)]">
                        <label
                          htmlFor="email-template-select"
                          className="block text-sm font-semibold text-cfHeadingText mb-2"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Mail size={14} className="text-cfPrimary" />
                            {t("workspace:email_template")}
                          </span>
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Select
                              inputId="email-template-select"
                              name="email-template-select"
                              autoComplete="off"
                              options={templateOptions}
                              value={selectedTemplateOption}
                              onChange={handleTemplateChange}
                              isLoading={isLoadingTemplates}
                              isSearchable
                              placeholder={t("workspace:select_email_template")}
                              styles={templateSelectStyles}
                              menuPortalTarget={document.body}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const credentialId = isMultipleSelect
                                ? selectedRecipientId
                                : recipient?.id;
                              const selectedRecipientIds = isMultipleSelect
                                ? recipients.map((item) => item.id)
                                : credentialId
                                  ? [credentialId]
                                  : [];
                              navigate(
                                `/issuer/clone-email-template?from=${encodeURIComponent(`${location.pathname}${location.search || ""}`)}&templateId=${encodeURIComponent(selectedTemplateId || "")}`,
                                {
                                  state: {
                                    returnState: {
                                      ...(location.state || {}),
                                      reopenEmailPreviewModal: true,
                                      reopenCredentialId: credentialId,
                                      selectedRecipientIds,
                                      isMultipleSelect,
                                      createdTemplateId: selectedTemplateId,
                                      initialTemplateId: selectedTemplateId,
                                    },
                                  },
                                },
                              );
                            }}
                            className="px-3 py-2 h-[42px] min-w-[110px] bg-cfPrimary text-cfSecondary rounded-[8px] hover:bg-cfPrimaryDark transition-colors inline-flex items-center justify-center gap-2 font-medium"
                          >
                            {t("settings:clone")}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="from-email"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {t("workspace:from")}:
                        </label>
                        <input
                          id="from-email"
                          type="text"
                          value={emailMetadata?.from_email || ""}
                          readOnly
                          className="w-full px-3 py-2 border border-cfIssuedScreenSearchBorder rounded-lg bg-[#F8FAFC]"
                        />
                      </div>
                      {isEmailIdentity === false && (
                        <span className="flex">
                          <div className="flex w-full text-start justify-start items-center gap-2 p-2 bg-cfEmailWarningContainerBg rounded-lg">
                            <img
                              alt="Warning"
                              src={WarningIcon}
                              height={35}
                              width={35}
                            />
                            <div className="text-cfEmailWarningTextBg text-sm">
                              {emailIdentityMessage}
                            </div>
                          </div>
                        </span>
                      )}

                      <div className="email-to">
                        <label
                          htmlFor="to-email-input"
                          className="block text-sm font-medium text-cfHeadingText mb-2"
                        >
                          {t("workspace:to")}:
                        </label>
                        {isMultipleSelect ? (
                          <div
                            className="flex border border-cfIssuedScreenSearchBorder rounded-lg w-full"
                            onClick={() => {
                              setActiveField("to");
                              if (!currentMultiToEmail) {
                                toInputRef.current?.focus();
                              } else {
                                toInputRef.current?.focus();
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setActiveField("to");
                                toInputRef.current?.focus();
                              }
                            }}
                          >
                            <div className="flex items-start gap-2 flex-wrap p-2 bg-cfSecondary rounded-lg min-h-[50px]">
                              {recipientToEmails[selectedRecipientId]?.map(
                                (email) => (
                                  <div
                                    key={email}
                                    className="bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                                  >
                                    {email}
                                    <button
                                      className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                                      onClick={() =>
                                        handleRemoveMultiToEmail(email)
                                      }
                                    >
                                      <IoCloseCircleOutline size={20} />
                                    </button>
                                  </div>
                                ),
                              )}
                              <div className="w-fit flex gap-2 items-center">
                                <input
                                  ref={toInputRef}
                                  id="to-email-input"
                                  name={`to-email-${selectedRecipientId || "multi"}`}
                                  type="email"
                                  autoComplete="section-to email"
                                  placeholder="recipient@example.com"
                                  value={currentMultiToEmail}
                                  onChange={handleRecipientEmailChange}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleAddMultiToEmail()
                                  }
                                  className={`${
                                    currentMultiToEmail
                                      ? "border-2 border-cfPrimary"
                                      : "border-0"
                                  } focus:border-2 focus:border-cfPrimary rounded-full focus:outline-cfPrimary text-gray-700 ${
                                    !currentMultiToEmail
                                      ? "placeholder:text-gray-300"
                                      : "placeholder:text-gray-400"
                                  } px-2 py-1`}
                                />
                                {!multiToEmailError && currentMultiToEmail && (
                                  <button
                                    className="rounded-full bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1"
                                    onClick={handleAddMultiToEmail}
                                  >
                                    {t("workspace:add_recipient")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex border border-cfIssuedScreenSearchBorder rounded-lg w-full">
                            <div className="flex items-start gap-2 flex-wrap p-2 bg-cfSecondary rounded-lg min-h-[50px]">
                              {toEmails.map((email) => (
                                <div
                                  key={email}
                                  className="bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                                >
                                  {email}
                                  <button
                                    className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                                    onClick={() => onRemoveToEmail(email)}
                                  >
                                    <IoCloseCircleOutline size={20} />
                                  </button>
                                </div>
                              ))}
                              <div className="w-fit flex gap-2 items-center">
                                <input
                                  id="to-email-input"
                                  name="to-email-single"
                                  type="email"
                                  autoComplete="section-to email"
                                  placeholder="recipient@example.com"
                                  value={currentToEmail}
                                  onChange={onToEmailChange}
                                  onFocus={() => setActiveField("to")}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && onAddToEmail()
                                  }
                                  onPaste={onPasteToEmail}
                                  className={`${
                                    currentToEmail
                                      ? "border-2 border-cfPrimary"
                                      : "border-0"
                                  } focus:border-2 focus:border-cfPrimary rounded-full focus:outline-cfPrimary text-gray-700 ${
                                    !currentToEmail
                                      ? "placeholder:text-gray-300"
                                      : "placeholder:text-gray-400"
                                  } px-2 py-1`}
                                />
                                {!toEmailError && currentToEmail && (
                                  <button
                                    className="rounded-full bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1"
                                    onClick={onAddToEmail}
                                  >
                                    {t("workspace:add_recipient")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {(toEmailError || multiToEmailError) && (
                          <p className="text-red-500 mt-1 text-sm">
                            {toEmailError || multiToEmailError}
                          </p>
                        )}
                      </div>

                      <div className="email-cc">
                        <label
                          htmlFor="cc-email-input"
                          className="block text-sm font-medium text-cfHeadingText mb-2"
                        >
                          CC:
                        </label>
                        {isMultipleSelect ? (
                          <div
                            className="flex border border-cfIssuedScreenSearchBorder rounded-lg w-full"
                            onClick={() => {
                              setActiveField("cc");
                              if (!currentMultiCcEmail) {
                                ccInputRef.current?.focus();
                              } else {
                                ccInputRef.current?.focus();
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setActiveField("cc");
                                ccInputRef.current?.focus();
                              }
                            }}
                          >
                            <div className="flex items-start gap-2 flex-wrap p-2 bg-cfSecondary rounded-lg min-h-[50px]">
                              {recipientCcEmails[selectedRecipientId]?.map(
                                (email) => (
                                  <div
                                    key={email}
                                    className="bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                                  >
                                    {email}
                                    <button
                                      className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                                      onClick={() =>
                                        handleRemoveMultiCcEmail(email)
                                      }
                                    >
                                      <IoCloseCircleOutline size={20} />
                                    </button>
                                  </div>
                                ),
                              )}
                              <div className="w-fit flex gap-2 items-center">
                                <input
                                  ref={ccInputRef}
                                  id="cc-email-input"
                                  name={`cc-email-${selectedRecipientId || "multi"}`}
                                  type="email"
                                  autoComplete="section-cc email"
                                  placeholder="cc@example.com"
                                  value={currentMultiCcEmail}
                                  onChange={handleRecipientCcEmailChange}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleAddMultiCcEmail()
                                  }
                                  className={`${
                                    currentMultiCcEmail
                                      ? "border-2 border-cfPrimary"
                                      : "border-0"
                                  } focus:border-2 focus:border-cfPrimary rounded-full focus:outline-cfPrimary text-gray-700 ${
                                    !currentMultiCcEmail
                                      ? "placeholder:text-gray-300"
                                      : "placeholder:text-gray-400"
                                  } px-2 py-1`}
                                />
                                {!multiCcEmailError && currentMultiCcEmail && (
                                  <button
                                    className="rounded-full bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1"
                                    onClick={handleAddMultiCcEmail}
                                  >
                                    {t("workspace:add_recipient")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex border border-cfIssuedScreenSearchBorder rounded-lg w-full">
                            <div className="flex items-start gap-2 flex-wrap p-2 bg-cfSecondary rounded-lg min-h-[50px]">
                              {ccEmails.map((email) => (
                                <div
                                  key={email}
                                  className="bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                                >
                                  {email}
                                  <button
                                    className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                                    onClick={() => onRemoveCcEmail(email)}
                                  >
                                    <IoCloseCircleOutline size={20} />
                                  </button>
                                </div>
                              ))}
                              <div className="w-fit flex gap-2 items-center">
                                <input
                                  id="cc-email-input"
                                  name="cc-email-single"
                                  type="email"
                                  autoComplete="section-cc email"
                                  placeholder="cc@example.com"
                                  value={currentCcEmail}
                                  onChange={onCcEmailChange}
                                  onFocus={() => setActiveField("cc")}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && onAddCcEmail()
                                  }
                                  onPaste={onPasteCcEmail}
                                  className={`${
                                    currentCcEmail
                                      ? "border-2 border-cfPrimary"
                                      : "border-0"
                                  } focus:border-2 focus:border-cfPrimary rounded-full focus:outline-cfPrimary text-gray-700 ${
                                    !currentCcEmail
                                      ? "placeholder:text-gray-300"
                                      : "placeholder:text-gray-400"
                                  } px-2 py-1`}
                                />
                                {!ccEmailError && currentCcEmail && (
                                  <button
                                    className="rounded-full bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary px-2 py-1"
                                    onClick={onAddCcEmail}
                                  >
                                    {t("workspace:add_recipient")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {(ccEmailError || multiCcEmailError) && (
                          <p className="text-red-500 mt-1 text-sm">
                            {ccEmailError || multiCcEmailError}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="email-subject"
                          className="block text-sm font-medium text-cfHeadingText mb-2"
                        >
                          {t("workspace:subject")}:
                        </label>
                        <input
                          id="email-subject"
                          type="text"
                          value={emailMetadata?.email_subject || ""}
                          readOnly
                          className="w-full px-3 py-2 border border-cfIssuedScreenSearchBorder rounded-lg bg-[#F8FAFC]"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full flex flex-col lg:flex-1 overflow-hidden">
                  <div className="px-3 mt-3 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {t("workspace:email_preview")}
                    </h3>
                  </div>
                  <div className="p-6 overflow-y-auto">
                    {isLoading ? (
                      <PreviewSkeleton />
                    ) : (
                      <div
                        className="prose prose-sm min-w-[800px] lg:min-w-full max-w-none overflow-x-auto rounded-xl border border-cfIssuedScreenSearchBorder bg-cfSecondary p-4"
                        style={{
                          color: "inherit",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            emailPreview.replace(
                              /<a href=/g,
                              '<a style="color: blue; text-decoration: underline;" href=',
                            ),
                          ),
                        }}
                      />
                    )}
                  </div>
                  {emailMetadata?.attached_file &&
                    emailMetadata?.attachment && (
                      <div className="px-6 pb-6">
                        <span className="flex border border-cfIssuedScreenSearchBorder rounded-xl px-4 py-3 bg-cfSecondary">
                          <div className="w-[9.6rem] flex items-center">
                            <p className="flex items-center font-semibold gap-2">
                              {" "}
                              <ImAttachment /> {t("workspace:attachment")}
                            </p>
                            <p className="font-semibold text-gray-300 px-3">
                              |
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              const newWindow = window.open(
                                emailMetadata?.attached_file,
                                "_blank",
                                "noopener,noreferrer",
                              );
                              if (newWindow) newWindow.opener = null;
                            }}
                            className="rounded-[0.63rem] flex gap-2 items-center p-2 text-sm hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none ml-auto"
                            title="Click to preview attachment"
                          >
                            <FiFileText className="size-5 text-cfPrimaryDark" />
                            <span>{emailMetadata?.attachment}</span>
                            <ExternalLinkIcon
                              size={16}
                              className="inline-flex text-gray-600"
                              aria-hidden
                            />
                          </button>
                        </span>
                      </div>
                    )}
                </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 border-t border-grey-200 bg-[#F8FAFC]">
                <button
                  onClick={onClose}
                  id="notify-email-preview-modal-cancel"
                  className="px-4 py-2 border border-cfIssuedScreenSearchBorder rounded-md text-cfHeadingText hover:bg-gray-100 transition-colors"
                >
                  {t("workspace:cancel")}
                </button>
                <button
                  onClick={handleSendClick}
                  id="notify-email-preview-modal-send"
                  disabled={isSending}
                  className={`px-4 py-2 bg-cfPrimary text-cfSecondary rounded-md hover:bg-cfPrimaryDark transition-colors flex items-center gap-2 ${
                    isSending ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                >
                  <Mail className="h-4 w-4" />
                  {isSending
                    ? t("workspace:sending")
                    : `${t("workspace:send_email")}${isMultipleSelect ? "s" : ""}`}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default EmailPreviewModal;
